"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { toast, Toaster as Sonner, type ToasterProps } from "sonner";
import { useTheme } from "next-themes";
import { useMediaQuery } from "usehooks-ts";
import { Icon, type IconName } from "@/components/ui/icon";
import { LINEAR_CLASSNAME } from "@/components/ui/button";
import { ApiError, getApiErrorMessage } from "@/lib/api/error";
import { cn } from "@/lib/utils";

type ToastType =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "default"
  | "loading";

interface ArbiToastAction {
  label: string;
  onClick: () => void;
}

interface ArbiToastProps extends VariantProps<typeof toastVariants> {
  id: string | number;
  type: ToastType;
  title: string;
  description?: string;
  action?: ArbiToastAction;
  duration?: number;
}

const TYPE_ICONS: Record<ToastType, IconName> = {
  success: "IconCheckCircle2",
  error: "IconCircleX",
  warning: "IconExclamationCircle",
  info: "IconCircleInfo",
  default: "IconCircleDashed",
  loading: "IconLoadingCircle",
};

const toastVariants = cva(
  cn(
    "flex flex-col items-stretch rounded-lg font-sans",
    "w-80 max-sm:w-[calc(100vw-32px)]",
    "dark:shadow-[0_8px_24px_rgba(0,0,0,0.5)]",
    "before:from-(--toast-accent)/20 before:dark:from-(--toast-accent)/50 dark:before:from-(--toast-accent)/20 before:to-(--toast-accent)/5",
    "border-composition",
    LINEAR_CLASSNAME,
  ),
  {
    variants: {
      type: {
        success: "[--toast-accent:#a6f363]",
        error: "[--toast-accent:#ff8080]",
        warning: "[--toast-accent:#f3d163]",
        info: "[--toast-accent:#9bd4f8]",
        default: "[--toast-accent:#e0acff]",
        loading: "[--toast-accent:var(--heading)]",
      },
    },
    defaultVariants: {
      type: "default",
    },
  },
);

function ProgressBar({ duration }: { duration: number }) {
  const [width, setWidth] = useState(100);
  const startRef = useRef<number>(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setWidth(remaining);
      if (remaining > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration]);

  return (
    <div className="relative h-[3px] w-full overflow-hidden bg-contrast/4 rounded-full">
      <div
        className="absolute top-0 left-0 h-full rounded-full bg-[var(--toast-accent)] transition-none"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

function ArbiToast({
  id,
  type,
  title,
  description,
  action,
  duration = 3000,
}: ArbiToastProps) {
  const iconName = TYPE_ICONS[type];
  const dismiss = useCallback(() => toast.dismiss(id), [id]);
  const hasBody = description || action || type !== "loading";

  return (
    <div className={toastVariants({ type })}>
      <div className="text-foreground dark:text-[var(--toast-accent)] flex items-center gap-1.5 p-3 bg-muted dark:bg-surface-elevated bg-[image:linear-gradient(180deg,transparent_0%,color-mix(in_srgb,var(--toast-accent)_20%,transparent)_100%)] dark:bg-[image:linear-gradient(180deg,transparent_0%,color-mix(in_srgb,var(--toast-accent)_10%,transparent)_100%)]">
        <Icon
          name={iconName}
          size={16}
          className={cn("shrink-0 ", type === "loading" && "animate-spin")}
        />
        <p className="min-w-0 flex-1 text-pretty text-[15px] font-medium leading-normal">
          {title}
        </p>
        <button
          onClick={dismiss}
          className="shrink-0 flex items-center justify-center w-4 h-4 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
        >
          <Icon name="IconCrossMedium" size={16} className="text-heading" />
        </button>
      </div>

      {hasBody && (
        <div
          className={cn(
            "flex flex-col gap-3 items-start px-3 py-2.5",
            "z-2 bg-muted dark:bg-surface-elevated border-t border-[var(--border-alpha)]",
          )}
        >
          {description && (
            <p className="w-full text-[13px] font-normal leading-normal tracking-[-0.26px] text-heading opacity-80">
              {description}
            </p>
          )}
          {action && (
            <button
              onClick={() => {
                action.onClick();
                dismiss();
              }}
              className={cn(
                "flex items-center justify-center gap-1.5 w-full px-3.5 py-1 rounded-full overflow-hidden",
                "cursor-pointer transition-colors",
                "bg-surface-header border border-[var(--border-subtle-alpha)]",
                "hover:bg-surface-hover",
              )}
            >
              <span className="text-xs font-normal leading-normal text-heading opacity-70">
                {action.label}
              </span>
              <Icon
                name="IconSquareArrowTopRight2"
                size={13}
                className="shrink-0 text-heading opacity-70"
              />
            </button>
          )}
          {Number.isFinite(duration) && <ProgressBar duration={duration} />}
        </div>
      )}
    </div>
  );
}

export type ToastPosition =
  | "top-left"
  | "top-right"
  | "top-center"
  | "bottom-left"
  | "bottom-right"
  | "bottom-center";

function fireToast(
  type: ToastType,
  title: string,
  options?: {
    description?: string;
    action?: ArbiToastAction;
    duration?: number;
    position?: ToastPosition;
    id?: string | number;
    onDismiss?: () => void;
  },
) {
  const duration = options?.duration ?? 3000;
  // Only pass `id` when set. Sonner's toast.custom does
  // `create({ id: generated, ...data })` — an explicit `id: undefined`
  // overwrites the generated id, create() allocates a different one, and
  // dismiss(returnedId) never clears the toast (loading toasts hang forever).
  return toast.custom(
    (id) => (
      <ArbiToast
        id={id}
        type={type}
        title={title}
        description={options?.description}
        action={options?.action}
        duration={duration}
      />
    ),
    {
      ...(options?.id !== undefined ? { id: options.id } : {}),
      duration,
      unstyled: true,
      position: options?.position,
      onDismiss: options?.onDismiss,
    },
  );
}

async function firePromiseToast<T>(
  input: Promise<T> | (() => Promise<T>),
  messages: {
    loading: string;
    success: string | ((value: T) => string);
    error: string | ((error: unknown) => string);
    description?: string;
    successDescription?: string | ((value: T) => string | undefined);
    errorDescription?: string | ((error: unknown) => string | undefined);
  },
): Promise<T> {
  const promise = typeof input === "function" ? input() : input;
  const toastId = fireToast("loading", messages.loading, {
    description: messages.description,
    duration: Infinity,
  });

  try {
    const value = await promise;
    toast.dismiss(toastId);
    fireToast(
      "success",
      typeof messages.success === "function"
        ? messages.success(value)
        : messages.success,
      {
        description:
          typeof messages.successDescription === "function"
            ? messages.successDescription(value)
            : messages.successDescription,
      },
    );
    return value;
  } catch (error) {
    toast.dismiss(toastId);
    const errorDescription =
      typeof messages.errorDescription === "function"
        ? messages.errorDescription(error)
        : messages.errorDescription ?? getApiErrorMessage(error);

    fireToast(
      "error",
      typeof messages.error === "function"
        ? messages.error(error)
        : messages.error,
      { description: errorDescription },
    );
    throw error;
  }
}

/**
 * Fire-and-forget variant of `showToast.promise` for callers that discard
 * the returned promise (`void showToast.promise(...)`). Those calls never
 * attach a `.catch`, so a rethrown *expected* business error (a 4xx
 * `ApiError` — cap/quota/rate-limit) becomes an unhandled promise rejection
 * that Better Stack reports as an urgent production error, even though the
 * user already saw the toast. This swallows only that expected case; real
 * failures (5xx, network errors, anything not an `ApiError`) still rethrow
 * so callers that DO await/catch (save-preset, sheet, breadcrumbs — which
 * use `showToast.promise` directly) and error monitoring keep seeing them.
 */
async function fireAndForget<T>(
  input: Promise<T> | (() => Promise<T>),
  messages: Parameters<typeof firePromiseToast<T>>[1],
): Promise<void> {
  try {
    await firePromiseToast(input, messages);
  } catch (error) {
    if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
      return;
    }
    throw error;
  }
}

interface ToastOptions {
  description?: string;
  action?: ArbiToastAction;
  duration?: number;
  position?: ToastPosition;
  id?: string | number;
  onDismiss?: () => void;
}

export const showToast = {
  success: (title: string, options?: ToastOptions) =>
    fireToast("success", title, options),
  error: (title: string, options?: ToastOptions) =>
    fireToast("error", title, options),
  warning: (title: string, options?: ToastOptions) =>
    fireToast("warning", title, options),
  info: (title: string, options?: ToastOptions) =>
    fireToast("info", title, options),
  loading: (title: string, options?: Omit<ToastOptions, "action">) =>
    fireToast("loading", title, options),
  message: (title: string, options?: ToastOptions) =>
    fireToast("default", title, options),
  promise: firePromiseToast,
  fireAndForget,
  dismiss: toast.dismiss,
};

/**
 * Whether the toast is up and in front. Sonner stacks toasts by when they
 * first showed, and an update keeps its place in the stack.
 */
export function isFrontToast(id: string | number): boolean {
  return toast.getToasts().at(-1)?.id === id;
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { resolvedTheme } = useTheme();
  const isMobile = useMediaQuery("(max-width: 640px)");
  return (
    <Sonner
      theme={(resolvedTheme as "dark" | "light") ?? "dark"}
      position={isMobile ? "bottom-center" : "top-center"}
      toastOptions={{ duration: 3000 }}
      {...props}
    />
  );
};

export { Toaster, toastVariants };
